import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { Chip } from '@cbhq/cds-web/chips';
import { VStack } from '@cbhq/cds-web/layout';
import { Fallback } from '@cbhq/cds-web/layout/Fallback';
import type { PopoverContentPositionConfig } from '@cbhq/cds-web/overlays';
import { PopoverPanel } from '@cbhq/cds-web/overlays';
import { TextCaption } from '@cbhq/cds-web/typography/TextCaption';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { noop } from '@cbhq/identity-shared/utils';

import type { Organization } from '../contexts/OrganizationContext';

type WorkspaceOrganizationPickerProps = {
  /** List of organizations. Undefined means still loading. */
  organizations: Organization[] | undefined;
  /** The currently selected organization. */
  selectedOrganization: Organization | undefined;
  /** Callback when user selects an organization */
  selectOrganization: (organizationId: string) => void;
};

type OrganizationListCellProps = {
  organization: Organization;
  isSelected: boolean;
  onSelect: (organizationId: string) => void;
  closePopover: () => void;
};

const messages = defineMessages({
  header: {
    id: 'WorkspaceOrganizationPicker.header',
    defaultMessage: 'Your organizations',
    description: 'Header label above the organization list in the picker dropdown',
  },
  entityCount: {
    id: 'WorkspaceOrganizationPicker.entityCount',
    defaultMessage: '{count, plural, one {# entity} other {# entities}}',
    description: 'Number of legal entities in an organization',
  },
});

const contentPosition: PopoverContentPositionConfig = {
  placement: 'bottom-end',
  gap: 1,
};

// Clip overflow on the outer PopoverPanelContent wrapper so the inner VStack owns scrolling.
// Without this, the panel's own overflow="auto" + maxPanelHeight produces a native scrollbar
// on top of the one inside the content function.
const panelContentStyles = { overflow: 'hidden' } as const;
const panelStyles = { content: panelContentStyles } as const;

function OrganizationListCell({
  organization,
  isSelected,
  onSelect,
  closePopover,
}: OrganizationListCellProps) {
  const { formatMessage } = useIntl();

  const handleClick = useCallback(() => {
    onSelect(organization.organizationId);
    closePopover();
  }, [onSelect, organization.organizationId, closePopover]);

  return (
    <ListCell
      title={organization.organizationName}
      description={
        organization.legalEntities.length > 1
          ? formatMessage(messages.entityCount, { count: organization.legalEntities.length })
          : undefined
      }
      spacingVariant="condensed"
      selected={isSelected}
      accessory={isSelected ? 'selected' : 'unselected'}
      onClick={handleClick}
    />
  );
}

export function WorkspaceOrganizationPicker({
  organizations,
  selectedOrganization,
  selectOrganization,
}: WorkspaceOrganizationPickerProps) {
  const { formatMessage } = useIntl();

  const handleOrganizationChange = useCallback(
    (organizationId: string) => {
      if (organizationId !== selectedOrganization?.organizationId) {
        logEvent('workspace_organization_switched', {
          componentType: ComponentType.dropdown,
          action: ActionType.select,
        });
      }
      selectOrganization(organizationId);
    },
    [selectOrganization, selectedOrganization?.organizationId],
  );

  const renderContent = useCallback(
    ({ closePopover }: { closePopover: () => void }) => (
      <VStack overflow="hidden">
        <TextCaption
          as="span"
          color="fgMuted"
          paddingX={3}
          paddingTop={2}
          paddingBottom={1}
          textTransform="uppercase"
        >
          {formatMessage(messages.header)}
        </TextCaption>
        <VStack className="workspaceHiddenScrollbar" flexGrow={1} minHeight={0} overflow="auto">
          {organizations?.map((org) => (
            <OrganizationListCell
              key={org.organizationId}
              organization={org}
              isSelected={org.organizationId === selectedOrganization?.organizationId}
              onSelect={handleOrganizationChange}
              closePopover={closePopover}
            />
          ))}
        </VStack>
      </VStack>
    ),
    [formatMessage, organizations, selectedOrganization?.organizationId, handleOrganizationChange],
  );

  if (!selectedOrganization || organizations === undefined) {
    return <Fallback width={200} height={40} shape="circle" />;
  }

  if (organizations.length === 0) {
    return null;
  }

  return (
    <PopoverPanel
      content={renderContent}
      accessibilityLabel={formatMessage(messages.header)}
      testID="WorkspaceOrganizationPicker-Dropdown"
      enableMobileModal
      contentPosition={contentPosition}
      panelWidth={320}
      maxPanelHeight={400}
      showOverlay
      styles={panelStyles}
    >
      {/* PopoverPanel requires an interactive trigger; Chip becomes interactive with onClick. */}
      <Chip maxWidth={200} height={40} onClick={noop}>
        {selectedOrganization.organizationName}
      </Chip>
    </PopoverPanel>
  );
}
